/**
 * Попереднє виявлення чутливих даних у тексті — дзеркало перевірки сервера
 * (apps/api/src/platform/security.ts → detectSensitive). Остаточно вирішує сервер;
 * інтерфейс лише попереджає заздалегідь і пропонує замаскувати.
 */
export type SensitiveKind = 'card' | 'iban' | 'password';

export const SENSITIVE_LABELS: Record<SensitiveKind, string> = {
  card: 'номер платіжної картки',
  iban: 'IBAN',
  password: 'пароль',
};

function luhn(num: string): boolean {
  let sum = 0;
  let dbl = false;
  for (let i = num.length - 1; i >= 0; i--) {
    let d = Number(num[i]);
    if (dbl) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    dbl = !dbl;
  }
  return sum % 10 === 0;
}

const CARD_RE = /\b(?:\d[ -]?){13,19}\b/g;
const IBAN_RE = /\bUA\s?(?:\d\s?){27}\b/gi;
const PASSWORD_RE = /((?:пароль|password|passwd|pwd)\s*[:=]\s*)(\S+)/gi;

export function detectSensitive(text: string): SensitiveKind[] {
  const found = new Set<SensitiveKind>();
  const digits = text.replace(/[^\d ]/g, ' ');
  for (const m of digits.matchAll(CARD_RE)) {
    const num = m[0].replace(/\D/g, '');
    if (num.length >= 13 && num.length <= 19 && luhn(num)) {
      found.add('card');
      break;
    }
  }
  if (/\bUA\d{27}\b/i.test(text.replace(/\s/g, ''))) found.add('iban');
  if (/(пароль|password|passwd|pwd)\s*[:=]\s*\S+/i.test(text)) found.add('password');
  return [...found];
}

/** Маскує знайдені дані: картка й IBAN — лишаються останні 4 цифри, пароль — повністю. */
export function maskSensitive(text: string): string {
  let out = text.replace(CARD_RE, (m) => {
    const num = m.replace(/\D/g, '');
    if (num.length < 13 || num.length > 19 || !luhn(num)) return m;
    return `•••• ${num.slice(-4)}`;
  });
  out = out.replace(IBAN_RE, (m) => {
    const num = m.replace(/\s/g, '');
    return `UA•• •••• ${num.slice(-4)}`;
  });
  out = out.replace(PASSWORD_RE, (_m, prefix: string) => `${prefix}••••••`);
  return out;
}

export function describeSensitive(kinds: SensitiveKind[]): string {
  return kinds.map((k) => SENSITIVE_LABELS[k]).join(', ');
}
